/**
 * Aircraft Custodian flights client + mapper (verified against the live ELB
 * 2026-08-26: 439 records on tenant `airmarket`).
 *
 * Two endpoints back one list:
 *   GET /api/v1/flights           — everything, paged, limit caps at 200
 *   GET /api/v1/flights/reserved  — the planned/scheduled subset
 * A reserved flight ALSO appears in the search result, so the two are merged
 * by `internal_guid` with the reserved record winning (it is the one carrying
 * `planned: true` and the scheduled window).
 *
 * Plain-HTTP + no CORS → same-origin `/custodian-api` proxy, which injects
 * X-Tenant-Id server-side. Bare `fetch`, NOT the shared `http` ky instance —
 * `http` tags failures as expected-and-droppable for Sentry, which is wrong
 * for a real integration. Same choice as `custodian.ts`.
 */
import { config } from '@/lib/config';
import type {
  Flight,
  FlightAltitudeBand,
  FlightLastKnown,
  FlightPolygon,
  FlightStatus,
  FlightTrackPoint,
} from './types';

/** Raw record shape as returned by both flight endpoints — verified live
 *  2026-08-26 across a 200-record sample. Every field is nullable in
 *  practice; only `internal_guid` and `aircraft_serial` were populated on
 *  every record. */
export interface CustodianFlight {
  internal_guid: string;
  tenant_id: string | null;
  aircraft_serial: string;
  callsign: string | null;
  flight_guid: string | null;
  pilot_id: string | null;
  mission_name: string | null;
  mission_main_flight_type: string | null;
  mission_location: string | null;
  mission_notes: string | null;
  drone_model: string | null;
  drone_type: string | null;
  drone_status: string | null;
  drone_callsign: string | null;
  drone_notes: string | null;
  pilot_full_name: string | null;
  pilot_legal_profile: string | null;
  /** TWO shapes in the wild — see `normalizeOperationalVolume`. */
  operational_volume: unknown;
  /** `[lat, lon, alt_m]` — NOT Mapbox order. */
  waypoints: Array<[number, number, number | null]> | null;
  uas_id: string | null;
  /** Remote-ID registration fields, added to the response 2026-08-27 (§2.1).
   *  `uas_id_type` matches `UAS_ID_TYPE` on the OI declaration MQTT payload. */
  uas_id_type: number | null;
  uas_type: number | null;
  timestamp: string | null;
  lat: number | null;
  lon: number | null;
  geodetic_altitude: number | null;
  pressure_altitude: number | null;
  horizontal_speed: number | null;
  vertical_speed: number | null;
  heading: number | null;
  battery: number | null;
  alt_asl: number | null;
  alt_agl: number | null;
  /** Telemetry origin: 'aircraft_agent' | 'fh2' | 'dji' | 'dronesense' | null. */
  source: string | null;
  /** 1 = currently flying. 198 of 200 sampled records were 0. */
  flight_status: number | null;
  created_at: string | null;
  last_message_at: string | null;
  updated_at: string | null;
  mode: string | null;
  armed: number | boolean | null;
  utm_declaration_id: string | null;
  /** 'ended' | 'active' | 'cancelled' | 'rejected' | null. */
  utm_state: string | null;
  planned: boolean | null;
  dock_sn: string | null;
  task_scheduled_begin: string | null;
  task_scheduled_end: string | null;
  fh2_status_code: number | null;
  fh2_status_label: string | null;
}

interface FlightsPage {
  total: number;
  limit: number;
  offset: number;
  items: CustodianFlight[];
}

/** The endpoint's hard cap (OpenAPI `maximum: 200`). */
const PAGE_LIMIT = 200;
/** Backstop so a bad `total` cannot spin forever. 200 × 50 = 10k records. */
const MAX_PAGES = 50;

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

function boolOrNull(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  return null;
}

/** Seconds between two timestamps, or null if either is missing/unparseable.
 *  The API returns naive ISO strings with no zone (`2026-08-26T05:25:12.87`);
 *  both ends are parsed the same way, so the difference is correct regardless
 *  of how the runtime interprets the zone. */
function durationBetween(a: string | null, b: string | null): number | null {
  if (!a || !b) return null;
  const ms = Date.parse(b) - Date.parse(a);
  if (!Number.isFinite(ms) || ms < 0) return null;
  return Math.round(ms / 1000);
}

/**
 * `operational_volume` arrives in TWO different shapes on adjacent records:
 *
 *   A) GeoJSON FeatureCollection whose first Polygon feature carries a
 *      `properties.{min,max}_altitude` band.
 *   B) A bare ring array `[[[lng,lat], …]]` with no band at all.
 *
 * Both were observed live on 2026-08-26. Reading only one shape is exactly
 * the failure that blanked the drone-detail pane in 0.2.0, so this handles
 * both and returns nulls (never throws) for anything unrecognised.
 */
export function normalizeOperationalVolume(v: unknown): {
  polygon: FlightPolygon | null;
  band: FlightAltitudeBand | null;
} {
  const none = { polygon: null, band: null };
  if (v == null) return none;

  // Shape B — bare ring array.
  if (Array.isArray(v)) {
    const rings = v.filter(
      (r): r is number[][] =>
        Array.isArray(r) && r.length > 0 && r.every((p) => Array.isArray(p) && p.length >= 2),
    );
    if (rings.length === 0) return none;
    return {
      polygon: {
        type: 'Polygon',
        coordinates: rings.map((r) => r.map((p) => [p[0] as number, p[1] as number])),
      },
      band: null,
    };
  }

  // Shape A — FeatureCollection.
  if (typeof v !== 'object') return none;
  const fc = v as { features?: unknown };
  if (!Array.isArray(fc.features)) return none;
  const feature = fc.features.find(
    (f): f is { geometry?: { type?: string; coordinates?: unknown }; properties?: unknown } =>
      typeof f === 'object' && f !== null,
  );
  const geom = feature?.geometry;
  if (geom?.type !== 'Polygon' || !Array.isArray(geom.coordinates)) return none;

  const coordinates = geom.coordinates
    .filter((r): r is unknown[] => Array.isArray(r) && r.length > 0)
    .map((r) =>
      r
        .filter((p): p is number[] => Array.isArray(p) && p.length >= 2)
        .map((p) => [p[0] as number, p[1] as number] as [number, number]),
    )
    .filter((r) => r.length > 0);
  if (coordinates.length === 0) return none;

  const props = (feature?.properties ?? {}) as {
    min_altitude?: { meters?: number; datum?: string };
    max_altitude?: { meters?: number; datum?: string };
  };
  const min = num(props.min_altitude?.meters);
  const max = num(props.max_altitude?.meters);
  const band: FlightAltitudeBand | null =
    min != null && max != null
      ? { min_m: min, max_m: max, datum: props.max_altitude?.datum ?? 'W84' }
      : null;

  return { polygon: { type: 'Polygon', coordinates }, band };
}

/**
 * Status precedence: planned > active > aborted > completed.
 *
 * `planned` wins outright — a scheduled flight that also carries a stale
 * `flight_status: 1` is still a plan, not a flight in progress.
 */
export function deriveFlightStatus(r: CustodianFlight): FlightStatus {
  if (r.planned === true) return 'Planned';
  if (r.flight_status === 1 || r.utm_state === 'active') return 'Active';
  // ENG-302 — RTM is off for this aircraft, so no OI was declared: still waiting,
  // not finished. (A flight already in the air is caught by the line above.)
  if (r.utm_state === 'held') return 'Planned';
  if (r.utm_state === 'cancelled' || r.utm_state === 'rejected') return 'Aborted';
  return 'Completed';
}

function lastKnownOf(r: CustodianFlight): FlightLastKnown | null {
  const lat = num(r.lat);
  const lon = num(r.lon);
  if (lat == null || lon == null) return null;
  // The API writes -1 for "no battery telemetry". A literal -1% would be
  // nonsense on a gauge, so it becomes null and renders '—'.
  const battery = num(r.battery);
  return {
    lat,
    lon,
    alt_asl_m: num(r.alt_asl),
    alt_agl_m: num(r.alt_agl),
    heading_deg: num(r.heading),
    horizontal_speed_ms: num(r.horizontal_speed),
    vertical_speed_ms: num(r.vertical_speed),
    battery_pct: battery != null && battery >= 0 ? battery : null,
    armed: boolOrNull(r.armed),
    mode: strOrNull(r.mode),
    at: strOrNull(r.timestamp),
  };
}

export function mapCustodianFlight(r: CustodianFlight): Flight {
  const planned = r.planned === true;
  const { polygon, band } = normalizeOperationalVolume(r.operational_volume);

  // NOTE: Live has no measured flight window. For a historical flight,
  // start_at/end_at come from created_at / last_message_at, which are RECORD
  // timestamps — when the row was written and when it last received a
  // message — not when the aircraft took off and landed. A flight whose
  // telemetry kept arriving after landing reads long; one whose row was
  // created before takeoff reads early. We surface it with
  // duration_approx: true and the UI marks it (see the duration cell in
  // features/flights/columns.ts) rather than presenting it as measured.
  // When the time-series DB lands, derive the window from the first/last
  // track point and set duration_approx: false.
  const start = planned ? strOrNull(r.task_scheduled_begin) : strOrNull(r.created_at);
  const end = planned ? strOrNull(r.task_scheduled_end) : strOrNull(r.last_message_at);

  return {
    flight_id: r.internal_guid,
    aircraft_serial: r.aircraft_serial,
    source: planned ? 'planned' : 'historical',
    connector: strOrNull(r.source),
    flight_status: deriveFlightStatus(r),
    aircraft_status: strOrNull(r.drone_status),

    start_at: start,
    end_at: end,
    duration_s: durationBetween(start, end),
    duration_approx: !planned,

    // Flight-level aggregates. `/api/v1/telemetry` returns raw points and
    // carries none of these in its `metadata`, and they are deliberately NOT
    // computed from the points: a number we calculated here would be
    // indistinguishable in the UI from one the backend measured. They stay
    // null (never 0, which reads as measured) until BE adds them. Only
    // `data_point_count` gets filled, from that response's own `count`, when
    // a detail view loads it — see `fetchFlightTelemetry`.
    total_distance_m: null,
    max_alt_wgs84_m: null,
    max_alt_asl_m: null,
    max_alt_agl_m: null,
    data_point_count: null,

    mission_name: strOrNull(r.mission_name),
    mission_type: strOrNull(r.mission_main_flight_type),
    mission_location: strOrNull(r.mission_location),
    pilot_name: strOrNull(r.pilot_full_name),
    utm_state: strOrNull(r.utm_state),
    utm_declaration_id: strOrNull(r.utm_declaration_id),
    dock_sn: strOrNull(r.dock_sn),
    fh2_status_label: strOrNull(r.fh2_status_label),
    scheduled_begin: strOrNull(r.task_scheduled_begin),
    scheduled_end: strOrNull(r.task_scheduled_end),

    // API gives [lat, lon, alt]; Mapbox wants [lng, lat]. Flip here, once,
    // so no call site has to remember. Same hazard the repo documents for
    // VITE_DEFAULT_MAP_CENTER.
    waypoints:
      r.waypoints?.map((w) => [w[1], w[0], w[2] ?? null] as [number, number, number | null]) ??
      null,
    operational_volume: polygon,
    altitude_band: band,
    last_known: lastKnownOf(r),

    // No DroneLogBook sync endpoint exists on the Custodian API, so live has
    // no sync state to report. The UI shows the pending-backend explainer.
    sync_status: 'Not Started',
    synced_at: null,

    // The list endpoint never carries telemetry. null = not loaded (the
    // detail view fetches it from `/api/v1/telemetry`); [] = a plan that has
    // not flown yet, so there is nothing to fetch.
    track: planned ? [] : null,
  };
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Custodian API ${res.status}`);
  return (await res.json()) as T;
}

/**
 * Just the reserved (planned) set — ONE small call (~2 KB).
 *
 * The nav badge needs a count, not the history. `NavRail` is mounted in every
 * context, so having it call `fetchFlights()` would pull the full paged list
 * (439 records / ~858 KB measured against stage on 2026-08-26) on every app
 * load, in every context, for a user who may never open Flights — and it
 * would grow with the tenant's flight history. Badge reads this; the Flights
 * context itself reads `fetchFlights`.
 */
export async function fetchReservedFlights(): Promise<Flight[]> {
  const res = await getJson<FlightsPage>(
    `${config.custodian.base}/api/v1/flights/reserved?limit=${PAGE_LIMIT}&offset=0`,
  );
  return res.items.map(mapCustodianFlight);
}

/**
 * Server-side filters `GET /flights` accepts (spec §2.1, 2026-08-27). Every
 * one is optional and they AND together; omitting all of them returns the
 * tenant's whole history.
 *
 * NOT here, and the reason this module still pages: `aircraft_serial`. The
 * Flights context is aircraft-first, so without it we fetch the entire set
 * and group client-side. See the note on `fetchFlights`.
 */
export interface FlightFilters {
  /** Exact match. */
  flight_guid?: string;
  /** Exact match on the row's primary key. */
  internal_guid?: string;
  /** Exact match, e.g. 'ended' | 'active' | 'cancelled' | 'rejected'. */
  utm_state?: string;
  /** Exact match. */
  utm_declaration_id?: string;
  /** Exact match on telemetry source, e.g. 'DJI Flight Hub' | 'Dronesense'. */
  source?: string;
  /** Range start on `created_at`. MUST be UTC — see `toUtcParam`. */
  from?: string | Date;
  /** Range end on `created_at`. MUST be UTC — see `toUtcParam`. */
  to?: string | Date;
}

/**
 * Render a `from`/`to` bound as a UTC ISO string.
 *
 * `flights.binding`'s timestamp columns are timezone-NAIVE and always hold
 * UTC. Per spec §2.1 the server takes a bare value as UTC as-is with no
 * conversion, so a local-time string silently queries the wrong window — an
 * hour or more off, with no error. Everything therefore goes through
 * `toISOString()`, which is always UTC, including strings: a `Date` round-trip
 * normalises `'2026-08-01'` or a local-offset ISO value to true UTC rather
 * than passing the caller's spelling through untouched.
 *
 * Returns null for an unparseable value so the caller drops the param
 * instead of sending garbage that would match nothing.
 */
export function toUtcParam(v: string | Date): string | null {
  const t = v instanceof Date ? v.getTime() : Date.parse(v);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toISOString();
}

/** Parse a `flights.binding` timestamp as UTC.
 *
 *  Those columns are `timestamp` (no timezone) and always hold UTC, so the
 *  wire value normally has no designator — `Date.parse` would read it in the
 *  browser's zone and shift it by the local offset. A `Z` or `±hh:mm` is
 *  honoured where present rather than being mangled, since the API has not
 *  been consistent about it across endpoints. Returns null when unparseable. */
function parseNaiveUtc(v: string | null): number | null {
  if (!v) return null;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(v);
  const t = Date.parse(hasZone ? v : `${v}Z`);
  return Number.isFinite(t) ? t : null;
}

/** Build the query string for one page of `GET /flights`. */
function flightsQuery(filters: FlightFilters, offset: number): string {
  const params = new URLSearchParams({ limit: String(PAGE_LIMIT), offset: String(offset) });
  for (const key of [
    'flight_guid',
    'internal_guid',
    'utm_state',
    'utm_declaration_id',
    'source',
  ] as const) {
    const v = filters[key];
    if (v != null && v !== '') params.set(key, v);
  }
  for (const key of ['from', 'to'] as const) {
    const v = filters[key];
    if (v == null || v === '') continue;
    const utc = toUtcParam(v);
    if (utc) params.set(key, utc);
  }
  return params.toString();
}

/**
 * Fetch flights for the tenant and merge in the reserved (planned) set.
 *
 * `filters` maps straight onto the server-side params added 2026-08-27
 * (spec §2.1). Narrowing there is strictly better than filtering the result:
 * it cuts both the payload and the number of pages we walk.
 *
 * NOTE: there is still no `aircraft_serial` filter. The Flights context is
 * aircraft-first, so an unfiltered load pages the ENTIRE set (439 records
 * ~= 3 calls at the 200 cap) and groups by `aircraft_serial` client-side.
 * That is O(tenant flight history) per load and will not hold as that history
 * grows — the time-range and `source` filters below reduce it but don't fix
 * it. If BE adds `?aircraft_serial=`, add it to `FlightFilters` and key the
 * query by serial. The BE ask itself has no ticket yet — raise one before
 * relying on it.
 *
 * `/flights/reserved` takes no filters by design (spec §2.2), so the planned
 * set is always fetched whole and then narrowed client-side to match —
 * otherwise a filtered query would still drag in every planned flight.
 */
export async function fetchFlights(filters: FlightFilters = {}): Promise<Flight[]> {
  const base = `${config.custodian.base}/api/v1/flights`;
  const items: CustodianFlight[] = [];

  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await getJson<FlightsPage>(`${base}?${flightsQuery(filters, offset)}`);
    items.push(...res.items);
    offset += PAGE_LIMIT;
    if (res.items.length === 0 || offset >= res.total) break;
  }

  const reserved = await getJson<FlightsPage>(`${base}/reserved?limit=${PAGE_LIMIT}&offset=0`);

  // A reserved flight also appears in the search result. Reserved wins — it
  // is the record carrying `planned: true` and the scheduled window.
  const byGuid = new Map<string, CustodianFlight>();
  for (const r of items) byGuid.set(r.internal_guid, r);
  for (const r of reserved.items) {
    if (!matchesFilters(r, filters)) continue;
    byGuid.set(r.internal_guid, r);
  }

  return [...byGuid.values()].map(mapCustodianFlight);
}

/**
 * Raw telemetry point / flight-level metadata.
 *
 * Both are bags of unknown keys rather than declared shapes, because this
 * endpoint decides at query time WHICH of the two a field lands in — see
 * `pickField`. The keys actually read are listed in `mapTelemetryPoint`.
 *
 * The response also carries several aliases of one value
 * (`hAcc`/`hacc`/`horizontal_accuracy`, `groundSpeed`/`horizontal_speed`,
 * `time`/`timestamp`/`timestamp_utc`). We read ONE spelling per value,
 * preferring the name `/api/v1/flights` already uses, and never fall back to
 * a differently-named sibling: that kind of chain silently absorbs a backend
 * rename, whereas a single read surfaces it as a visible `—`.
 */
type CustodianTelemetryPoint = Record<string, unknown>;
type CustodianTelemetryMetadata = Record<string, unknown>;

interface CustodianTelemetryResponse {
  identifier?: { field?: string; value?: string } | null;
  range?: { from?: string; to?: string } | null;
  count?: unknown;
  metadata?: CustodianTelemetryMetadata | null;
  points?: CustodianTelemetryPoint[] | null;
}

/**
 * Read one field from the point, falling back to `metadata`.
 *
 * NOT an alias chain — it is the SAME field name in the two places this
 * endpoint puts it. Any value that does not vary across the requested window
 * is hoisted out of every point into `metadata`, so which of the two holds a
 * field depends on the window you asked for: verified against sandbox
 * 2026-09-14, `flightMode` / `armed` / `battery` sit in `metadata` for this
 * flight, and `operational_volume` moves from `metadata` into `points` when
 * the window widens from 1 minute to 10. Reading only the point would render
 * `—` for a value the response plainly carries, which is the opposite of the
 * signal the dashes are there to send.
 */
function pickField(
  p: CustodianTelemetryPoint,
  meta: CustodianTelemetryMetadata,
  key: string,
): unknown {
  return p[key] ?? meta[key];
}

export interface FlightTelemetry {
  points: FlightTrackPoint[];
  /** The endpoint's own `count`, not `points.length`. If the two disagree
   *  that is a backend fact worth seeing, not something to reconcile here. */
  count: number | null;
}

/** Map one raw point onto `FlightTrackPoint`.
 *
 *  Every field is read through `pickField`, so a value hoisted into
 *  `metadata` because it is constant over the window still lands on the
 *  point. Fields the endpoint sends in NEITHER place stay null:
 *
 *  - `alt_wgs84` — absent. `geodetic_altitude` is present but is byte-equal
 *    to `alt_asl` on every sampled point, so it is not the ellipsoidal height
 *    and is not used as one. Approximating WGS84 from `alt_asl` plus a geoid
 *    offset would put a number we invented in a column the operator reads as
 *    measured.
 *  - `rssi_dbm` — absent, no analogue.
 *
 *  Both therefore render `—`, which is the signal to BE that they are
 *  missing. Do not "fix" the blank columns by deriving them. */
export function mapTelemetryPoint(
  p: CustodianTelemetryPoint,
  meta: CustodianTelemetryMetadata = {},
): FlightTrackPoint {
  // -1 is the API's "no battery telemetry" sentinel, same as on the flight
  // record (see `lastKnownOf`). A literal -1% would read as measured.
  const battery = num(pickField(p, meta, 'battery'));
  return {
    t: strOrNull(pickField(p, meta, 'time')),
    lat: num(pickField(p, meta, 'lat')),
    lon: num(pickField(p, meta, 'lon')),
    alt_wgs84: null,
    alt_asl: num(pickField(p, meta, 'alt_asl')),
    alt_agl: num(pickField(p, meta, 'alt_agl')),
    speed_ms: num(pickField(p, meta, 'horizontal_speed')),
    heading: num(pickField(p, meta, 'heading')),
    flight_mode: strOrNull(pickField(p, meta, 'flightMode')),
    armed: boolOrNull(pickField(p, meta, 'armed')),
    battery_pct: battery != null && battery >= 0 ? battery : null,
    rssi_dbm: null,
  };
}

/** Padding applied to the record window before it is used as query bounds.
 *
 *  `created_at` / `last_message_at` are RECORD timestamps, not a measured
 *  flight window (see the NOTE on `mapCustodianFlight`), so telemetry can
 *  fall outside them in either direction. Wide enough to absorb that skew,
 *  still far narrower than the endpoint's 90-day default. */
const WINDOW_PAD_MS = 6 * 3600_000;

/**
 * Query bounds for one flight's telemetry, from its own record window.
 *
 * Both bounds or neither: the endpoint's behaviour with only one supplied is
 * not specified, and a half-open range that silently means "and 90 days back
 * from here" is worse than taking the documented default outright.
 */
export function telemetryWindow(f: Flight): { from?: string; to?: string } {
  // parseNaiveUtc, not Date.parse — these timestamps usually arrive with no
  // zone designator and must be read as UTC, or the bounds shift by the
  // viewer's local offset and quietly return the wrong slice.
  const start = parseNaiveUtc(f.start_at);
  const end = parseNaiveUtc(f.end_at);
  if (start == null || end == null) return {};
  return {
    from: new Date(start - WINDOW_PAD_MS).toISOString(),
    to: new Date(end + WINDOW_PAD_MS).toISOString(),
  };
}

/**
 * `GET /api/v1/telemetry` — one flight's telemetry history out of InfluxDB.
 *
 * Takes exactly ONE identifier or it 400s; we send `internal_guid`, which is
 * what `Flight.flight_id` already holds in live mode.
 *
 * `from`/`to` are effectively required: omitted, the endpoint defaults to the
 * last 90 days, which returns nothing for an older flight and scans far more
 * than needed for a recent one. `telemetryWindow` builds them from the
 * flight's own record window.
 *
 * Rides the same `/custodian-api` proxy as `/api/v1/flights`. Roughly 10 s
 * and ~10k points per flight, which is why this is fetched per detail view
 * and never for the list.
 */
export async function fetchFlightTelemetry(
  internalGuid: string,
  window: { from?: string; to?: string } = {},
): Promise<FlightTelemetry> {
  const params = new URLSearchParams({ internal_guid: internalGuid });
  if (window.from) params.set('from', window.from);
  if (window.to) params.set('to', window.to);

  const res = await getJson<CustodianTelemetryResponse>(
    `${config.custodian.base}/api/v1/telemetry?${params}`,
  );
  const meta = res.metadata ?? {};
  return {
    points: (res.points ?? []).map((p) => mapTelemetryPoint(p, meta)),
    count: num(res.count),
  };
}

/** Apply `FlightFilters` client-side, for the reserved set only.
 *
 *  Mirrors the server's semantics: exact match on the string fields, an
 *  inclusive range on `created_at`. A record missing the field a filter names
 *  cannot match it — matching it would let an unpopulated row through a
 *  filter the user deliberately set. */
export function matchesFilters(r: CustodianFlight, filters: FlightFilters): boolean {
  const exact: Array<[string | undefined, string | null]> = [
    [filters.flight_guid, r.flight_guid],
    [filters.internal_guid, r.internal_guid],
    [filters.utm_state, r.utm_state],
    [filters.utm_declaration_id, r.utm_declaration_id],
    [filters.source, r.source],
  ];
  for (const [want, got] of exact) {
    if (want == null || want === '') continue;
    if (got !== want) return false;
  }

  if (filters.from != null || filters.to != null) {
    const at = parseNaiveUtc(r.created_at);
    if (at == null) return false;
    const from = filters.from != null ? toUtcParam(filters.from) : null;
    const to = filters.to != null ? toUtcParam(filters.to) : null;
    if (from && at < Date.parse(from)) return false;
    if (to && at > Date.parse(to)) return false;
  }
  return true;
}
